import type { ReactNode } from "react"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

interface Column<T> {
  key: keyof T | string
  label: string
  render?: (row: T) => ReactNode
  align?: "left" | "right"
}

interface Props<T> {
  title: string
  rows: T[]
  columns: Column<T>[]
  emptyLabel: string
}

/**
 * Pick a stable identifier for a usage row. The dashboard renders four
 * variants (top slides, top users, top decks, top searches) which each
 * carry a different identity field. Fall back through the known ones,
 * then to a JSON fingerprint. Index is the last resort and only used for
 * rows with no identifying field at all (shouldn't happen in practice).
 */
function rowKey(row: unknown, fallbackIndex: number): string {
  const r = row as Record<string, unknown> | null | undefined
  if (r == null) return `row-${fallbackIndex}`
  const candidates = ["slide_id", "deck_id", "user_id", "user_email", "query"]
  for (const k of candidates) {
    const v = r[k]
    if (typeof v === "string" && v) return `${k}:${v}`
  }
  return `row-${fallbackIndex}`
}

export function TopNTable<T>({ title, rows, columns, emptyLabel }: Props<T>) {
  return (
    <Card className="rounded-lg shadow-sm">
      <CardHeader className="p-5 pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-5 pt-0">
        {rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">{emptyLabel}</p>
        ) : (
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                {columns.map((c) => {
                  const isRight = c.align === "right"
                  return (
                    <TableHead
                      key={String(c.key)}
                      className={`font-normal ${isRight ? "w-24 text-right" : "text-left"}`}
                    >
                      {c.label}
                    </TableHead>
                  )
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={rowKey(row, i)}>
                  {columns.map((c) => {
                    const isRight = c.align === "right"
                    return (
                      <TableCell
                        key={String(c.key)}
                        className={
                          isRight
                            ? "text-right align-top tabular-nums"
                            : "text-left align-top break-words"
                        }
                      >
                        {c.render
                          ? c.render(row)
                          : ((row as Record<string, ReactNode>)[c.key as string] as ReactNode)}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}
